/* eslint-disable react/no-unused-prop-types */
import { CSSProperties } from 'styled-components';
import { RenderIf } from 'react-rainbow-components';
import Label from 'react-rainbow-components/components/Input/label';
import { useReduxForm, useUniqueIdentifier } from '@rainbow-modules/hooks';
import BuyPhoneNumberButton from 'components/BuyPhoneNumberButton';
import useHttpQuery from 'data/firestore/useHttpQuery';
import { isEmpty } from '@rainbow-modules/validation';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import { BUY_PHONE_FORM_MODAL } from '../../constants';
import {
    InputContainer, ErrorText, StyledLoadingShape, EmptyMessage,
    FieldContainer,
} from './styled';
import PhoneListInputItem from './item';

interface PhoneListInputProps {
    id?: string;
    className?: string;
    required?: boolean;
    style?: CSSProperties;
    value?: string;
    onChange?: (value: string | null) => void;
}

const PhoneListInput = (props: PhoneListInputProps) => {
    const {
        id,
        className,
        style,
        value,
        required = false,
        onChange = () => {},
        error,
    } = useReduxForm(props);

    const errorMessageId = useUniqueIdentifier('error-message');

    const {
        data: phoneNumbers = [],
        isLoading: isLoadingPhoneNumbers,
    } = useHttpQuery<Record<string, string>[]>({
        key: 'twilio-available-phone-numbers',
        pathname: '/phone-numbers/available',
    });

    const phoneNumbersList = phoneNumbers.map(
        (phone: Record<string, string>) => (
            <PhoneListInputItem
                key={phone.id}
                isSelected={value === phone.id}
                label={(
                    <>
                        <span>{formatIntlPhoneNumber(phone?.number)}</span>
                        <span>{phone?.location}</span>
                    </>
                )}
                value={phone.id}
                onChange={onChange}
                error={error}
            />
        ),
    );

    return (
        <div
            id={id}
            role="listbox"
            className={className}
            // eslint-disable-next-line react/forbid-dom-props
            style={style}
        >
            <FieldContainer>
                <Label required={required} label="Phone Number" />
                <BuyPhoneNumberButton
                    modalId={BUY_PHONE_FORM_MODAL}
                    borderRadius="semi-rounded"
                    variant="outline-brand"
                    size="small"
                />
            </FieldContainer>
            <InputContainer $error={Boolean(error)}>
                <RenderIf isTrue={isLoadingPhoneNumbers}>
                    <div>
                        <StyledLoadingShape variant="solid" shape="rounded-rect" $width="40%" />
                        <StyledLoadingShape variant="solid" shape="rounded-rect" $width="30%" />
                        <StyledLoadingShape variant="solid" shape="rounded-rect" $width="60%" />
                    </div>
                </RenderIf>
                <RenderIf isTrue={!isLoadingPhoneNumbers}>
                    {phoneNumbersList}
                    <RenderIf isTrue={isEmpty(phoneNumbers)}>
                        <EmptyMessage>
                            There are no phone numbers available. Please buy a new one.
                        </EmptyMessage>
                    </RenderIf>
                </RenderIf>
            </InputContainer>
            <RenderIf isTrue={Boolean(error)}>
                <ErrorText id={errorMessageId}>
                    {error}
                </ErrorText>
            </RenderIf>
        </div>
    );
};

export default PhoneListInput;
